import { ref } from 'vue'
import {
  generateMarkupGeoJson,
  generateShapeLabelsGeoJson,
  generateWaypointLabelGeoJson,
} from '@/lib/markup-geojson'
import { mapReadyPromise } from '@/services/map-service'
import {
  isWaypoint,
  isLine,
  isTrack,
  isShape,
  isRoute,
  getMarkupType,
} from '@/services/markup-service/helpers'
import { MarkupType } from '@/types/markups'

/**
 * Service for managing rendered markups on the map. Caches GeoJSON for all markups, including their default,
 * highlighted, and focused states. This class is purposefully not reactive, and avoids querying any reactive
 * arrays or objects, to optimize performance for accounts with a large amount of markup geometry.
 */
class MarkupRenderService {
  constructor() {
    this._markups = {}
    this._highlightedMarkupIds = new Set()
    this._visibleMarkupIds = new Set()
    this._focusedMarkupId = null
    // using arrays without reactive (ref) wrapper to optimize performance
    this._waypointsFeatures = []
    this._linesFeatures = []
    this._tracksFeatures = []
    this._shapesFeatures = []
    this._routesFeatures = []
    this.clearCache()
    // flags to trigger re-rendering of markups
    this._waypointsNeedsUpdate = ref(false)
    this._linesNeedsUpdate = ref(false)
    this._tracksNeedsUpdate = ref(false)
    this._shapesNeedsUpdate = ref(false)
    this._routesNeedsUpdate = ref(false)
  }

  /**
   * Gets the flag indicating if waypoints need to be updated.
   */
  get waypointsNeedsUpdate() {
    return this._waypointsNeedsUpdate.value
  }

  /**
   * Sets the flag indicating if waypoints need to be updated.
   */
  set waypointsNeedsUpdate(value) {
    this._waypointsNeedsUpdate.value = value
  }

  /**
   * Gets the flag indicating if lines need to be updated.
   */
  get linesNeedsUpdate() {
    return this._linesNeedsUpdate.value
  }

  /**
   * Sets the flag indicating if lines need to be updated.
   */
  set linesNeedsUpdate(value) {
    this._linesNeedsUpdate.value = value
  }

  /**
   * Gets the flag indicating if tracks need to be updated.
   */
  get tracksNeedsUpdate() {
    return this._tracksNeedsUpdate.value
  }

  /**
   * Sets the flag indicating if tracks need to be updated.
   */
  set tracksNeedsUpdate(value) {
    this._tracksNeedsUpdate.value = value
  }

  /**
   * Gets the flag indicating if shapes need to be updated.
   */
  get shapesNeedsUpdate() {
    return this._shapesNeedsUpdate.value
  }

  /**
   * Sets the flag indicating if shapes need to be updated.
   */
  set shapesNeedsUpdate(value) {
    this._shapesNeedsUpdate.value = value
  }

  /**
   * Gets the flag indicating if routes need to be updated.
   */
  get routesNeedsUpdate() {
    return this._routesNeedsUpdate.value
  }

  /**
   * Sets the flag indicating if routes need to be updated.
   */
  set routesNeedsUpdate(value) {
    this._routesNeedsUpdate.value = value
  }

  /**
   * Sets the highlighted markup IDs and re-renders markups.
   * @param {Set} value - Set of markup ids
   */
  async setHighlightedMarkupIds(value) {
    this._highlightedMarkupIds = value
    await this.update()
  }

  /**
   * Sets the visible markup IDs and optionally re-renders markups.
   * @param value {array} Array of markup IDs
   * @param [skipUpdate] {boolean} If true, skip re-rendering
   */
  async setVisibleMarkupIds(value, skipUpdate = false) {
    this._visibleMarkupIds = new Set(value)
    if (!skipUpdate) {
      await this.update()
    }
  }

  /**
   * Sets the focused markup ID and re-renders markups.
   * @param markupId {String} Markup ID
   */
  async setFocusedMarkupId(markupId) {
    this._focusedMarkupId = markupId
    await this.update()
  }

  /**
   * Clears the focused markup and optionally re-renders markups.
   * @param [skipUpdate] {boolean} If true, skip re-rendering
   */
  async clearFocusedMarkup(skipUpdate = false) {
    this._focusedMarkupId = null
    this._highlightedMarkupIds = new Set()
    if (!skipUpdate) {
      await this.update()
    }
  }

  /**
   * Returns the cached GeoJSON for routes
   */
  getRoutesGeoJson() {
    return this._routesFeatures
  }

  /**
   * Returns the cached GeoJSON for waypoints (that are not highlighted)
   */
  getWaypointsGeoJson() {
    const wpGeoJson = this._waypointsFeatures.filter(
      (f) =>
        !f.properties?.highlighted && f.properties?.meta !== 'waypoint-label'
    )
    return wpGeoJson
  }

  /**
   * Returns the cached GeoJSON for waypoints labels
   */
  getWaypointLabelsGeoJson() {
    const wpGeoJson = this._waypointsFeatures.filter(
      (f) => f.properties?.meta === 'waypoint-label'
    )
    return wpGeoJson
  }

  /**
   * Returns the cached GeoJSON for waypoints that are highlighted
   */
  getHighlightedWaypointsGeoJson() {
    const hlWpGeoJson = this._waypointsFeatures.filter(
      (f) => f.properties?.highlighted
    )
    return hlWpGeoJson
  }

  /**
   * Returns the cached GeoJSON for lines
   */
  getLinesGeoJson() {
    return this._linesFeatures
  }

  /**
   * Returns the cached GeoJSON for tracks
   */
  getTracksGeoJson() {
    return this._tracksFeatures
  }

  /**
   * Returns the cached GeoJSON for shapes
   */
  getShapesGeoJson() {
    return this._shapesFeatures
  }

  /**
   * Adds a new markup
   * @param markup {Object} Markup object
   * @param [skipUpdate] {boolean} If true, skip re-rendering
   */
  async addMarkup(markup, skipUpdate = false) {
    await this.addMarkups([markup], skipUpdate)
  }

  /**
   * Adds multiple new markups
   * @param markups {array} Array of markup objects
   * @param [skipUpdate] {boolean} If true, skip re-rendering
   */
  async addMarkups(markups, skipUpdate = false) {
    for (const markup of markups) {
      this._markups[markup.uuid] = markup
    }
    if (!skipUpdate) {
      await this.update()
    }
  }

  /**
   * Updates an existing markup
   * @param markup {Object} Markup object
   */
  async updateMarkup(markup) {
    this._markups[markup.uuid] = markup
    delete this._cache.default[markup.uuid]
    delete this._cache.highlighted[markup.uuid]
    delete this._cache.focused[markup.uuid]
    await this.update()
  }

  /**
   * Removes an existing markup
   * @param markup {Object} Markup object
   */
  async removeMarkup(markup) {
    await this.removeMarkups([markup.uuid])
  }

  /**
   * Removes multiple markups
   * @param markupIds {array} Markup IDs to remove
   */
  async removeMarkups(markupIds) {
    const needsUpdateTypes = new Set()

    for (const id of markupIds) {
      const markup = this._markups[id]
      const markupType = getMarkupType(markup)
      if (markupType) {
        needsUpdateTypes.add(markupType)
      }
      delete this._markups[id]
      delete this._cache.default[id]
      delete this._cache.highlighted[id]
      delete this._cache.focused[id]
      this._visibleMarkupIds.delete(id)
      this._highlightedMarkupIds.delete(id)
    }

    await this.update(false, Array.from(needsUpdateTypes))
  }

  /**
   * Highlights a given markup
   * @param markup {object} Markup object
   */
  async highlightMarkup(markup) {
    this._highlightedMarkupIds.add(markup.uuid)
    await this.update()
  }

  /**
   * Removes highlights for a given markup
   * @param markup {object} Markup object
   */
  async removeMarkupHighlight(markup) {
    this._highlightedMarkupIds.delete(markup.uuid)
    await this.update()
  }

  /**
   * Clear all highlighted markups
   * @param skipUpdate {boolean} If true, skip re-rendering
   */
  async clearHighlightedMarkups(skipUpdate = false) {
    this._highlightedMarkupIds.clear()
    if (!skipUpdate) {
      await this.update()
    }
  }

  /**
   * Set visibility of a given markup
   * @param markupId {String} Markup ID
   * @param visible {boolean} Set to visible
   */
  async setVisibility(markupId, visible) {
    await this.bulkSetVisibility([markupId], visible)
  }

  /**
   * Bulk set visibility of multiple markups
   * @param markupIds {array} Array of markup IDs
   * @param visible {boolean} Whether to set the markups to visible or hide them
   */
  async bulkSetVisibility(markupIds, visible) {
    const op = visible ? 'add' : 'delete'
    const needsUpdateTypes = new Set()
    markupIds.forEach((id) => {
      const markup = this._markups[id]
      const markupType = getMarkupType(markup)
      if (markupType) {
        needsUpdateTypes.add(markupType)
      }
      // this is the equivalent of this._visibleMarkupIds.add(id)
      this._visibleMarkupIds[op](id)
    })
    await this.update(false, Array.from(needsUpdateTypes))
  }

  /**
   * Generates GeoJSON for a given markup with the given state
   * @param markup {object} Markup object
   * @param [focused] {boolean} Focused state (i.e., editing)
   * @param [highlighted] {boolean} Highlighted state
   * @return {array} Array of GeoJSON features for the markup
   */
  generateGeoJson(markup, focused = false, highlighted = false) {
    const markupGeoJson = generateMarkupGeoJson(markup, focused || highlighted)
    const features = [markupGeoJson]
    if (isWaypoint(markup)) {
      const waypointLabels = generateWaypointLabelGeoJson(markup)
      features.push(...waypointLabels)
    }
    if (isShape(markup) && focused) {
      const shapeLabels = generateShapeLabelsGeoJson(markup)
      features.push(...shapeLabels)
    }

    return features
  }

  /**
   * Translates highlighted / focused flags into the key to use in the _cache object
   * @param highlighted {boolean} Highlighted state
   * @param focused {boolean} Focused state
   * @return {string} State key for _cache object
   */
  _getState(highlighted, focused) {
    if (focused) {
      return 'focused'
    }
    return highlighted ? 'highlighted' : 'default'
  }

  /**
   * Gets or creates GeoJSON features for a given markup with the given state. If cached value is available,
   * it will be used.
   * @param markup {object} Markup object
   * @param focused {boolean} Focused state (i.e., editing)
   * @param highlighted {boolean} Highlighted state
   * @return {array} Array of GeoJSON features for the markup
   */
  getOrCreateFeatures(markup, highlighted, focused) {
    const state = this._getState(highlighted, focused)
    return (this._cache[state][markup.uuid] ??= this.generateGeoJson(
      markup,
      focused,
      highlighted
    ))
  }

  /**
   * Clears the GeoJSON cache completely.
   */
  clearCache() {
    this._cache = {
      default: {},
      highlighted: {},
      focused: {},
    }
  }

  /**
   * Sets the markup GeoJSON features to render them on the map.
   *
   * @async
   * @param {boolean} [clearCache=false] - Whether to clear the feature cache before updating
   * @param {Array} [needsUpdateTypes=[]] - Array of MarkupType values that need to be updated
   * @returns {Promise<void>} A promise that resolves when the update and render is complete
   *
   * @description This method:
   * - Optionally clears the feature cache
   * - Resets all feature arrays (waypoints, lines, tracks, shapes, routes)
   * - Iterates through visible markup items and generates features for non-focused items
   * - Categorizes features by markup type and marks corresponding types as needing update
   * - Renders the updated features to the map
   */
  async update(clearCache = false, needsUpdateTypes = []) {
    if (clearCache) {
      this.clearCache()
    }

    this._waypointsFeatures = []
    this._linesFeatures = []
    this._tracksFeatures = []
    this._shapesFeatures = []
    this._routesFeatures = []
    const needsUpdate = {
      waypoints: needsUpdateTypes.includes(MarkupType.waypoint),
      lines: needsUpdateTypes.includes(MarkupType.line),
      tracks: needsUpdateTypes.includes(MarkupType.track),
      shapes: needsUpdateTypes.includes(MarkupType.shape),
      routes: needsUpdateTypes.includes(MarkupType.route),
    }
    for (const id of this._visibleMarkupIds) {
      const markup = this._markups[id]
      if (!markup) {
        continue
      }
      const highlighted = this._highlightedMarkupIds.has(id)
      const focused = this._focusedMarkupId === id
      if (!focused) {
        const features = this.getOrCreateFeatures(markup, highlighted, focused)
        if (isWaypoint(markup)) {
          this._waypointsFeatures.push(...features)
          needsUpdate.waypoints = true
        } else if (isLine(markup)) {
          this._linesFeatures.push(...features)
          needsUpdate.lines = true
        } else if (isTrack(markup)) {
          this._tracksFeatures.push(...features)
          needsUpdate.tracks = true
        } else if (isShape(markup)) {
          this._shapesFeatures.push(...features)
          needsUpdate.shapes = true
        } else if (isRoute(markup)) {
          this._routesFeatures.push(...features)
          needsUpdate.routes = true
        }
      }
    }
    await this._render(needsUpdate)
  }

  /**
   * Notifies the map viewer to re-render markups after they have been updated
   * @param needsUpdate {Object} Object indicating which markup types need to be updated
   */
  async _render(needsUpdate) {
    await mapReadyPromise
    if (needsUpdate.waypoints) {
      this._waypointsNeedsUpdate.value = true
    }
    if (needsUpdate.lines) {
      this._linesNeedsUpdate.value = true
    }
    if (needsUpdate.tracks) {
      this._tracksNeedsUpdate.value = true
    }
    if (needsUpdate.shapes) {
      this._shapesNeedsUpdate.value = true
    }
    if (needsUpdate.routes) {
      this._routesNeedsUpdate.value = true
    }
  }
}

const markupRenderService = new MarkupRenderService()
export default markupRenderService
